/**
 * @deprecated Use ImageUploader from 'app/components/commons/ImageUploader/ImageUploader' instead.
 * This component uses axios and Redux, replaced by React Query for better state management.
 * Migration guide: Replace Uploader with ImageUploader component.
 */
import { message } from 'antd';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import type { UploadRequestOption } from 'rc-upload/es/interface';
import type { FC } from 'react';
import React, { useState } from 'react';

import { useAppSelector } from 'app/redux/hooks';
import { getFilename } from 'app/utils/files';

import type { Picture } from './Types';
import { UploadList } from './UploadList/UploadList';
import { UploadZone } from './UploadZone/UploadZone';

const omit = (
  key: string,
  obj: Record<string, number>
): Record<string, number> => {
  const { [key]: _omitted, ...rest } = obj;

  return rest;
};

const options = {
  maxSizeMB: 3,
  maxWidthOrHeight: 3000,
  useWebWorker: true,
};

export const Uploader: FC<Props> = ({
  pictures,
  add,
  remove,
  showHint,
  edit,
  previewable = false,
}) => {
  const apiUrl = useAppSelector((state) => state.navigation.apiUrl);
  const [uploadingItems, setUploadingItems] = useState<Record<string, number>>(
    {}
  );
  const upsertUploadingItem = (key: string, value: number) =>
    setUploadingItems((previousUploadingItems) => ({
      ...previousUploadingItems,
      [key]: value,
    }));
  const removeUploadingItem = (key: string) =>
    setUploadingItems((previousUploadingItems) =>
      omit(key, previousUploadingItems)
    );

  const uploadToS3 = async (
    formData: FormData,
    fileName: string,
    key: string
  ) => {
    try {
      const uploaded = await axios.post(`${apiUrl}/pictures`, formData, {
        onUploadProgress: (progress) =>
          upsertUploadingItem(key, progress.loaded / (progress.total ?? 1)),
      });

      if (uploaded.status !== 200) {
        throw new Error(uploaded.statusText);
      }

      return uploaded;
    } catch (error) {
      const errorMessage =
        (error as Error).message === 'Network Error'
          ? 'Network Error: connexion lost temporarily'
          : (error as Error).message;

      throw new Error(`Error while uploading ${fileName}: ${errorMessage}`);
    }
  };

  const upload = async ({ file }: UploadRequestOption) => {
    const rcFile = file as File & { uid: string };
    const key = `${rcFile.uid}`;

    try {
      // Compress file
      upsertUploadingItem(key, 0);

      const compressedFile = await imageCompression(rcFile, options);

      const fileName = getFilename(rcFile.name);
      const formData = new FormData();

      formData.append('file', compressedFile);

      const uploaded = await uploadToS3(formData, fileName, key);

      removeUploadingItem(key);

      if (pictures.find((pic) => pic.pictureId === uploaded.data.pictureId)) {
        throw new Error(`Same file already uploaded : ${fileName}`);
      }

      add(uploaded.data);
    } catch (error) {
      message.error((error as Error).message);
      removeUploadingItem(key);
    }
  };

  return (
    <>
      <UploadZone
        upload={upload}
        highlightedRequirement={pictures.length < 20}
        showHint={showHint}
      />
      <UploadList
        pictures={pictures}
        uploadingItems={uploadingItems}
        remove={remove}
        edit={edit}
        previewable={previewable}
        fromUploader
      />
    </>
  );
};

type Props = {
  pictures: Picture[];
  add: (picture: Picture) => void;
  showHint?: boolean;
  remove?: (uid: string) => void;
  edit?: (picture?: Picture) => void;
  previewable?: boolean;
};
